// src/components/common/Navbar/Navbar.js
import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars, faTimes, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import TransparentLogo from '../../../assets/images/TransparentLogo.svg';
import './Navbar.css';

const Navbar = ({ theme = 'default' }) => {
    const [isScrolled, setIsScrolled] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [showSchoolsDropdown, setShowSchoolsDropdown] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 50);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!event.target.closest('.mobile-menu') && !event.target.closest('.navbar-toggle')) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        setIsOpen(false);
        setShowSchoolsDropdown(false);
    }, [location.pathname]);

    // Lock body scroll when mobile menu is open
    useEffect(() => {
        if (isOpen) {
            document.body.classList.add('mobile-menu-open');
        } else {
            document.body.classList.remove('mobile-menu-open');
        }
        return () => document.body.classList.remove('mobile-menu-open');
    }, [isOpen]);

    const toggleMenu = () => setIsOpen(!isOpen);
    const toggleSchoolsDropdown = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setShowSchoolsDropdown(!showSchoolsDropdown);
    };

    const scrollToSection = (sectionId) => {
        setIsOpen(false);
        if (location.pathname === '/') {
            const section = document.getElementById(sectionId);
            if (section) {
                const yOffset = -80; // Adjust for navbar height
                const y = section.getBoundingClientRect().top + window.pageYOffset + yOffset;
                window.scrollTo({ top: y, behavior: 'smooth' });
            }
        } else {
            navigate('/#contact');
            setTimeout(() => {
                const section = document.getElementById(sectionId);
                if (section) {
                    const yOffset = -80;
                    const y = section.getBoundingClientRect().top + window.pageYOffset + yOffset;
                    window.scrollTo({ top: y, behavior: 'smooth' });
                }
            }, 100); // Delay to ensure page loads
        }
    };

    // Function to scroll to the top of the page
    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const isGreenButtonPage = ['/', '/about'].includes(location.pathname);

    return (
        <header className={`navbar ${isScrolled ? 'navbar-scrolled' : ''} navbar-${theme}`}>
            <div className="navbar-container">
                <div className="navbar-left">
                    <ul className="navbar-nav">
                        <li className="navbar-item">
                            <NavLink
                                to="/"
                                className={({ isActive }) => (isActive ? 'navbar-link active' : 'navbar-link')}
                                onClick={() => scrollToSection('hero')}
                            >
                                Home
                            </NavLink>
                        </li>
                        <li className="navbar-item">
                            <NavLink
                                to="/about"
                                className={({ isActive }) => (isActive ? 'navbar-link active' : 'navbar-link')}
                            >
                                About Us
                            </NavLink>
                        </li>
                        <li className="navbar-item dropdown-container">
                            <NavLink
                                to="/schools"
                                className={({ isActive }) => (isActive ? 'navbar-link active' : 'navbar-link')}
                                onClick={toggleSchoolsDropdown}
                            >
                                Our Schools
                                <FontAwesomeIcon
                                    icon={faChevronDown}
                                    className={`dropdown-icon ${showSchoolsDropdown ? 'rotated' : ''}`}
                                />
                            </NavLink>
                            <ul className={`dropdown-menu ${showSchoolsDropdown ? 'active' : ''}`}>
                                <li>
                                    <NavLink to="/schools/little-elly-kudlu" className="dropdown-link">
                                        Little Elly Kudlu
                                    </NavLink>
                                </li>
                                <li>
                                    <span className="dropdown-link coming-soon">More Schools Coming Soon</span>
                                </li>
                            </ul>
                        </li>
                    </ul>
                </div>

                <div className="navbar-logo">
                    <Link to="/" onClick={scrollToTop}>
                        <img src={TransparentLogo} alt="Abhyaas Logo" className="logo-image" />
                    </Link>
                </div>

                <div className="navbar-right">
                    <ul className="navbar-nav">
                        <li className="navbar-item">
                            <button
                                onClick={() => scrollToSection('contact')}
                                className="navbar-link"
                            >
                                Contact
                            </button>
                        </li>
                    </ul>

                    <div className="navbar-cta">
                        <Link
                            to="/schools/little-elly-kudlu"
                            className={`register-btn ${isGreenButtonPage ? 'register-btn-green' : ''}`}
                            onClick={() => window.scrollTo(0, 0)}
                        >
                            Admissions Open
                        </Link>
                    </div>
                </div>

                <button
                    className="navbar-toggle"
                    aria-label={isOpen ? 'Close menu' : 'Open menu'}
                    onClick={toggleMenu}
                >
                    <FontAwesomeIcon icon={isOpen ? faTimes : faBars} />
                </button>

                <div className={`mobile-menu ${isOpen ? 'active' : ''}`}>
                    <ul className="mobile-nav">
                        <li>
                            <NavLink to="/" onClick={() => scrollToSection('hero')}>
                                Home
                            </NavLink>
                        </li>
                        <li>
                            <NavLink to="/about">About Us</NavLink>
                        </li>
                        <li>
                            <NavLink to="/schools/little-elly-kudlu">Little Elly Kudlu</NavLink>
                        </li>
                        <li>
                            <button
                                onClick={() => scrollToSection('contact')}
                                className="mobile-nav-link"
                            >
                                Contact
                            </button>
                        </li>
                        <li className="mobile-cta">
                            <Link
                                to="/schools/little-elly-kudlu"
                                className={`mobile-register ${isGreenButtonPage ? 'mobile-register-green' : ''}`}
                                onClick={() => window.scrollTo(0, 0)}
                            >
                                Admissions Open
                            </Link>
                        </li>
                    </ul>
                </div>
            </div>
        </header>
    );
};

export default Navbar;